// What a time simulation shows about how well work flows: where queues build, which outputs fall behind and why, which
// roles need more capacity, and what to change. Built from a snapshot of the simulation, so it can be worked out
// while it runs and again in full when it stops

import { AIModelChange } from '../common/model-changes'
import { SimulationEvidence } from '../common/process-recommendations'

export const WORKING_SECONDS_PER_DAY = 8*3600
export const WORKING_HOURS_PER_YEAR = 8*5*48

// a step being looked at, as the simulation found it
export interface SimulatedStep {
    key: string
    name: string
    modelKey: string
    roleKeys: string[]
    manual: boolean                 // done by people rather than systems
    queue: number                   // items waiting or being worked on
    onhold: number                  // of those, items waiting where inputs join: for the next run with the least input, or for their counterparts
    maxQueue: number
    entered: number                 // items that reached the step
    completed: number               // items it finished
    durationSeconds: number         // the time to do one item
    people: number                  // how many can work on it at once (0 for no limit)
    secondsPerTickEach: number      // how much of each tick each of them works on it
    queueMark?: { tick: number, queue: number }   // the queue a day or so ago
    joinInputs?: { key: string, name: string, count: number }[]   // where it goes with its least input, what each input has sent
    waits?: boolean                 // items wait there with nobody working on them: never a queue, but a wait on the way
}

// an output (somewhere the work ends), as the simulation found it
export interface SimulatedOutput {
    key: string
    name: string
    modelKey: string
    produced: number
    itemsPerYear: number            // how many the model expects a year
    leadSeconds: number             // the average time from an input to the output
    workSeconds: number             // of that, the time spent working on it rather than waiting
    waitStepSeconds?: number        // and the time spent at steps where items wait, with nobody working on them
}

export interface SimulationSnapshot {
    tick: number
    ticksPerDay: number
    secondsPerTick: number
    yearFraction: number            // how much of the year has been simulated
    received: number                // items put in
    finished: boolean               // the year has been simulated
    steps: SimulatedStep[]
    outputs: SimulatedOutput[]
    predecessors: Map<string, string[]>     // the steps that send items to each step or output
}

// who does the work, from the organisation model
export interface RoleCapacity {
    name: string
    hours: number           // the hours of work a year
    capacity: number        // the hours a year it has for it
    people: number
}

export type HEALTH = 'good' | 'warning' | 'poor' | 'starting'

export type CAUSE = 'capacity' | 'people' | 'join' | 'bursts' | 'stalled'

export interface Bottleneck {
    key: string
    name: string
    model: string
    queue: number
    growthPerDay: number
    waitSeconds: number         // how long a newly arrived item waits, at the current rate (Infinity when nothing moves)
    cause: CAUSE
    explanation: string
    manual: boolean             // done by people rather than systems
    roles: string[]             // the roles doing it, by name
}

export interface ImpactedOutput {
    key: string
    name: string
    model: string
    produced: number
    expected: number
    delaySeconds: number        // the average time items spend waiting on the way
    waitStepSeconds: number     // of that, at steps where items wait with nobody working on them
    heldUpBy: { key: string, name: string }[]
    waitsAt: { key: string, name: string }[]      // those steps on the way, the longest wait first
    reason: string
}

export interface RoleNeed {
    key: string
    name: string
    utilisation: number         // work as a share of capacity
    extraHours: number          // the hours a year it's short by
    extraPeople: number         // people needed to clear its steps' queues
    steps: { key: string, name: string }[]
}

export interface Suggestion {
    text: string
    stepKey?: string
    change?: AIModelChange      // the change to the model that carries it out, so it can be tried in a draft project
}

// the system a step is automated with, when a suggestion to automate it is tried
export const PROPOSED_AUTOMATION = 'Proposed automation'

// a change to the model in the form Change with AI makes them
const modelChange = (change: Partial<AIModelChange> & Pick<AIModelChange, 'kind' | 'description'>): AIModelChange => ({
    action: null, model: null, role: null, newRole: null, system: null, newSystem: null, percent: null, people: null, cost: null, from: null, items: null, output: null, minutes: null, ...change,
})

export interface SimulationReport {
    health: HEALTH
    headline: string
    week: number
    day: number
    received: number
    produced: number
    expected: number
    queued: number
    bottlenecks: Bottleneck[]
    outputs: ImpactedOutput[]
    roles: RoleNeed[]
    suggestions: Suggestion[]
}

// queues smaller than this come and go as items arrive together, so aren't treated as a problem
const MIN_QUEUE = 5
// a queue growing by more than this a day isn't keeping up
const GROWING_PER_DAY = 1
// an output below this share of what's expected is falling behind
const BEHIND_SHARE = 0.8
// joins to suggest changes to one by one
const MAX_JOIN_SUGGESTIONS = 4
// an output only counts as behind once enough are expected to tell
const MIN_EXPECTED = 10

// a time in working hours, days and weeks, as people plan work
const unit = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`
export const formatWorkingTime = (seconds: number): string => {
    if (!isFinite(seconds)) return 'not moving'
    const minutes = seconds/60
    if (minutes < 60) return `${Math.max(1, Math.round(minutes))} min`
    const hours = minutes/60
    if (hours < 8) return unit(Math.round(hours*10)/10, 'hour')
    const days = hours/8
    if (days < 5) return unit(Math.round(days*10)/10, 'day')
    return unit(Math.round(days/5*10)/10, 'week')
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`
const people = (n: number) => `${n} ${n === 1 ? 'person' : 'people'}`

const quoted = (name: string) => `“${name}”`

// where a step needs an item from every input, the input holding it back and the most any other has sent
const shortInput = (step: SimulatedStep) => {
    const inputs = step.joinInputs || []
    if (inputs.length < 2) return undefined
    const input = inputs.reduce((least, i) => i.count < least.count ? i : least)
    const most = Math.max(...inputs.filter(i => i !== input).map(i => i.count))
    return most > input.count ? { input, most } : undefined
}

const listNames = (names: string[]) => names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length-1]}`

export const buildSimulationReport = (snapshot: SimulationSnapshot, roles: Map<string, RoleCapacity>, models: Map<string, string>): SimulationReport => {
    const { tick, ticksPerDay, secondsPerTick } = snapshot
    const elapsedTicks = Math.max(1, tick - 1)
    const elapsedDays = elapsedTicks/ticksPerDay
    const dayNumber = Math.floor(elapsedDays)
    const modelName = (key: string) => models.get(key) || ''

    // growth is only meaningful after a few hours of work
    const starting = elapsedDays < 0.5

    const bottlenecks: Bottleneck[] = []
    snapshot.steps.forEach(step => {
        // items at a wait aren't held up by work: the report says where they wait, with the outputs
        if (step.waits || step.queue < MIN_QUEUE) return
        const mark = step.queueMark
        const growthPerDay = mark && tick > mark.tick ? (step.queue - mark.queue)/((tick - mark.tick)/ticksPerDay) : step.queue/elapsedDays
        const ratePerTick = step.completed/elapsedTicks
        const waitSeconds = ratePerTick > 0 ? step.queue/ratePerTick*secondsPerTick : Infinity
        const growing = !starting && growthPerDay > GROWING_PER_DAY
        // a queue that clears within a day and isn't growing is just work arriving together
        if (!growing && waitSeconds < WORKING_SECONDS_PER_DAY) return

        const demandPerTick = step.entered/elapsedTicks*step.durationSeconds        // seconds of work arriving each tick
        const supplyPerTick = (step.people || 1)*step.secondsPerTickEach
        let cause: CAUSE
        let explanation: string
        if (step.onhold >= step.queue/2) {
            cause = 'join'
            const short = shortInput(step)
            explanation = short
                ? `Goes with its least input, ${quoted(short.input.name)}, which has sent ${short.input.count.toLocaleString()} against ${short.most.toLocaleString()} from the others, so items wait for its next run`
                : `${plural(step.onhold, 'item')} wait for the next run, which goes once every input has sent something`
        }else if (step.completed === 0) {
            cause = 'stalled'
            explanation = `Nothing has been finished yet: each item takes ${formatWorkingTime(step.durationSeconds)}`
        }else if (step.secondsPerTickEach < secondsPerTick) {
            cause = 'capacity'
            explanation = `Its role has more work than capacity, so it only gets ${Math.round(step.secondsPerTickEach/secondsPerTick*100)}% of their time`
        }else if (step.people > 0 && demandPerTick > supplyPerTick*0.95) {
            cause = 'people'
            explanation = `More work arrives than ${people(step.people)} can do at once`
        }else {
            cause = 'bursts'
            explanation = 'Work arrives in bursts faster than it is done'
        }
        bottlenecks.push({ key: step.key, name: step.name, model: modelName(step.modelKey), queue: step.queue, growthPerDay, waitSeconds, cause, explanation,
            manual: step.manual, roles: step.roleKeys.map(k => roles.get(k)?.name).filter((n): n is string => !!n) })
    })
    // the longest waits first, as they hold up the most
    bottlenecks.sort((a, b) => (b.waitSeconds === a.waitSeconds ? b.queue - a.queue : b.waitSeconds - a.waitSeconds))
    const bottleneckKeys = new Map(bottlenecks.map(b => [b.key, b]))

    // the bottlenecks on the way to an output, nearest first
    const upstreamBottlenecks = (key: string) => {
        const found: Bottleneck[] = []
        const seen = new Set<string>([key])
        const queue = [key]
        while (queue.length && found.length < 3) {
            ;(snapshot.predecessors.get(queue.shift()!) || []).forEach(p => {
                if (seen.has(p)) return
                seen.add(p)
                queue.push(p)
                const b = bottleneckKeys.get(p)
                if (b) found.push(b)
            })
        }
        return found.slice(0, 3).map(b => ({ key: b.key, name: b.name }))
    }

    // the steps on the way to an output where items wait, the longest wait first
    const stepsByKey = new Map(snapshot.steps.map(s => [s.key, s]))
    const upstreamWaits = (key: string) => {
        const found: SimulatedStep[] = []
        const seen = new Set<string>([key])
        const queue = [key]
        while (queue.length) {
            ;(snapshot.predecessors.get(queue.shift()!) || []).forEach(p => {
                if (seen.has(p)) return
                seen.add(p)
                queue.push(p)
                const step = stepsByKey.get(p)
                if (step?.waits) found.push(step)
            })
        }
        return found.sort((a, b) => b.durationSeconds - a.durationSeconds).slice(0, 3).map(s => ({ key: s.key, name: s.name }))
    }

    let produced = 0
    let expectedTotal = 0
    const outputs: ImpactedOutput[] = []
    snapshot.outputs.forEach(output => {
        produced += output.produced
        const expected = output.itemsPerYear*snapshot.yearFraction
        expectedTotal += expected
        const delaySeconds = Math.max(0, output.leadSeconds - output.workSeconds)
        const behind = expected >= MIN_EXPECTED && output.produced < expected*BEHIND_SHARE
        const slow = output.produced > 0 && delaySeconds > WORKING_SECONDS_PER_DAY
        if (starting || (!behind && !slow)) return
        outputs.push({ key: output.key, name: output.name, model: modelName(output.modelKey), produced: output.produced, expected: Math.round(expected),
            delaySeconds, waitStepSeconds: output.waitStepSeconds || 0, heldUpBy: [], waitsAt: [], reason: '' })
    })
    // the furthest behind first
    const shortfall = (o: ImpactedOutput) => o.expected ? 1 - o.produced/o.expected : 0
    outputs.sort((a, b) => shortfall(b) - shortfall(a) || b.delaySeconds - a.delaySeconds)
    outputs.slice(0, 25).forEach(o => {
        o.heldUpBy = upstreamBottlenecks(o.key)
        o.waitsAt = o.waitStepSeconds > 0 ? upstreamWaits(o.key) : []
        // what holds it up most: queues at steps that can't keep up, or the waits on the way
        const waits = o.waitsAt.length ? `items wait ${formatWorkingTime(o.waitStepSeconds)} at ${listNames(o.waitsAt.map(s => quoted(s.name)))}` : ''
        o.reason = o.heldUpBy.length ? `Held up by ${listNames(o.heldUpBy.map(s => quoted(s.name)))}${waits ? `, and ${waits}` : ''}`
            : waits && o.waitStepSeconds >= o.delaySeconds/2 ? waits.charAt(0).toUpperCase() + waits.slice(1) + ', with nobody working on them'
            : o.expected && o.produced < o.expected*BEHIND_SHARE ? 'Fewer items reach it than the model expects: check the inputs and percentages that feed it'
            : 'Items wait along the way'
    })

    // the roles behind steps short of capacity or people
    const roleNeeds = new Map<string, RoleNeed>()
    bottlenecks.filter(b => b.cause === 'capacity' || b.cause === 'people').forEach(b => {
        const step = stepsByKey.get(b.key)!
        step.roleKeys.forEach(roleKey => {
            const role = roles.get(roleKey)
            if (!role) return
            let need = roleNeeds.get(roleKey)
            if (!need) {
                need = { key: roleKey, name: role.name, utilisation: role.capacity ? role.hours/role.capacity : 0,
                    extraHours: Math.max(0, Math.round(role.hours - role.capacity)), extraPeople: 0, steps: [] }
                roleNeeds.set(roleKey, need)
            }
            need.steps.push({ key: b.key, name: b.name })
            if (b.cause === 'people') {
                const demandPerTick = step.entered/elapsedTicks*step.durationSeconds
                need.extraPeople += Math.max(1, Math.ceil(demandPerTick/step.secondsPerTickEach) - step.people)
            }
        })
    })
    // only roles that are short: one can hold up a step at a busy time while having enough capacity over the year
    const roleList = [...roleNeeds.values()].filter(r => r.extraHours > 0 || r.extraPeople > 0).sort((a, b) => b.extraHours - a.extraHours || b.extraPeople - a.extraPeople)

    const suggestions: Suggestion[] = []
    roleList.forEach(r => {
        const steps = listNames(r.steps.slice(0, 3).map(s => quoted(s.name)))
        if (r.extraHours > 0) {
            const fte = Math.round(r.extraHours/WORKING_HOURS_PER_YEAR*10)/10
            // enough people for its work, at the capacity each of them has now
            const role = roles.get(r.key)!
            const now = role.people || 1
            const needed = Math.ceil(now*role.hours/role.capacity)
            suggestions.push({ stepKey: r.steps[0].key, text: `Give ${r.name} about ${r.extraHours.toLocaleString()} more hours a year (${fte} full-time ${fte === 1 ? 'person' : 'people'}): it has ${Math.round(r.utilisation*100)}% of the work it has capacity for, which holds up ${steps}.`,
                change: modelChange({ kind: 'staff', role: r.name, people: needed, description: `${r.name} has ${people(needed)} rather than ${now}` }) })
        }else if (r.extraPeople > 0) {
            const now = roles.get(r.key)!.people || 1
            suggestions.push({ stepKey: r.steps[0].key, text: `Have ${people(r.extraPeople).replace(' ', ' more ')} from ${r.name} work on ${steps} at the same time, or split the work, so it keeps up with what arrives.`,
                change: modelChange({ kind: 'staff', role: r.name, people: now + r.extraPeople, description: `${r.name} has ${people(now + r.extraPeople)} rather than ${now}` }) })
        }
    })
    bottlenecks.filter(b => b.cause === 'people' && !stepsByKey.get(b.key)!.roleKeys.some(k => roleNeeds.has(k))).forEach(b => {
        suggestions.push({ stepKey: b.key, text: `Put more people on ${quoted(b.name)}, or split it into smaller steps: more work arrives than can be done at once.` })
    })
    // joins are reported one by one up to a point, then together, as models can have many
    const joins = bottlenecks.filter(b => b.cause === 'join')
    joins.slice(0, MAX_JOIN_SUGGESTIONS).forEach(b => {
        const short = shortInput(stepsByKey.get(b.key)!)
        suggestions.push({ stepKey: b.key, text: short
            ? `${quoted(b.name)} goes with its least input, ${quoted(short.input.name)}, taking what the others have sent with each run, so items wait for its next run. Check how often ${quoted(short.input.name)} sends, or whether ${quoted(b.name)} should take each item as it comes.`
            : `${quoted(b.name)} only runs once every input has sent something. Check the percentages sent to it, or whether it needs all of its inputs.` })
    })
    if (joins.length > MAX_JOIN_SUGGESTIONS) {
        suggestions.push({ text: `${plural(joins.length - MAX_JOIN_SUGGESTIONS, 'more step')} wait for their least input in the same way: see the steps where work waits.` })
    }
    bottlenecks.filter(b => b.cause === 'stalled').forEach(b => {
        suggestions.push({ stepKey: b.key, text: `${quoted(b.name)} takes ${formatWorkingTime(stepsByKey.get(b.key)!.durationSeconds)} an item and hasn't finished any yet. Check its duration, or break it into smaller steps.` })
    })
    // manual steps with the longest waits could be automated
    bottlenecks.filter(b => stepsByKey.get(b.key)!.manual && b.cause !== 'join' && b.waitSeconds >= WORKING_SECONDS_PER_DAY).slice(0, 3).forEach(b => {
        suggestions.push({ stepKey: b.key, text: `Consider automating ${quoted(b.name)} (${b.model}): items wait ${formatWorkingTime(b.waitSeconds)} there.`,
            change: modelChange({ kind: 'automate', action: b.name, model: b.model, system: PROPOSED_AUTOMATION, description: `${quoted(b.name)} is automated` }) })
    })

    // the waits holding up outputs the most: shortening them gets the outputs out sooner, as nothing else can
    const waitsHoldingUp = new Map<string, ImpactedOutput[]>()
    outputs.filter(o => o.waitStepSeconds >= o.delaySeconds/2).forEach(o => o.waitsAt.slice(0, 1).forEach(w => (waitsHoldingUp.get(w.key) || waitsHoldingUp.set(w.key, []).get(w.key)!).push(o)))
    ;[...waitsHoldingUp.entries()].slice(0, 3).forEach(([key, held]) => {
        const step = stepsByKey.get(key)!
        const model = modelName(step.modelKey)
        suggestions.push({ stepKey: key, text: `Items wait ${formatWorkingTime(step.durationSeconds)} at ${quoted(step.name)} with nobody working on them, holding up ${listNames(held.slice(0, 3).map(o => quoted(o.name)))}. Shorten the wait: agree a turnaround, approve within limits automatically, or run it more often.`,
            change: modelChange({ kind: 'retime', action: step.name, model, percent: 50, description: `Items wait half as long at ${quoted(step.name)}` }) })
    })

    const queued = snapshot.steps.reduce((sum, s) => sum + s.queue, 0)
    // items waiting at a join for its least input go with its next run rather than leaving people behind, so they don't count as work backing up
    const behind = bottlenecks.filter(b => b.cause !== 'join')
    const growing = behind.filter(b => b.growthPerDay > GROWING_PER_DAY)
    // outputs well behind because of steps that can't keep up; those that just get fewer items than expected are a modelling question
    const farBehind = outputs.filter(o => o.heldUpBy.length && o.expected && o.produced < o.expected/2)

    let health: HEALTH
    let headline: string
    if (starting) {
        health = 'starting'
        headline = 'Getting going: queues are only judged after a few hours of work.'
    }else if (farBehind.length || growing.some(b => b.growthPerDay > 10) || behind.some(b => !isFinite(b.waitSeconds))) {
        health = 'poor'
        headline = [
            behind.length ? `Work is backing up: ${plural(behind.length, 'step')} can't keep up` : 'Outputs are falling behind',
            joins.length ? `${plural(joins.length, 'step')} ${joins.length === 1 ? 'waits' : 'wait'} for ${joins.length === 1 ? 'its' : 'their'} least input` : '',
            outputs.length ? `${plural(outputs.length, 'output')} ${outputs.length === 1 ? 'is' : 'are'} affected` : '',
        ].filter(s => s).join(', ') + '.'
    }else if (bottlenecks.length || outputs.length) {
        health = 'warning'
        headline = [
            'Mostly flowing',
            behind.length ? `${plural(behind.length, 'step')} where work waits` : '',
            joins.length ? `${plural(joins.length, 'step')} waiting for ${joins.length === 1 ? 'its' : 'their'} least input` : '',
            outputs.length ? `${plural(outputs.length, 'output')} held up` : '',
        ].filter(s => s).join(', ') + '.'
    }else {
        health = 'good'
        headline = 'Running smoothly: work moves through without lasting queues.'
    }
    if (!starting && !suggestions.length) {
        suggestions.push({ text: health === 'good' ? 'No changes needed: every step keeps up with the work it gets.' : 'Look at the steps where work waits to see whether their duration, people or inputs are right.' })
    }

    return {
        health, headline,
        week: Math.floor(dayNumber/5) + 1, day: dayNumber%5 + 1,
        received: Math.round(snapshot.received), produced, expected: Math.round(expectedTotal), queued,
        bottlenecks, outputs, roles: roleList, suggestions,
    }
}

// what a simulation showed, in the plain figures AI is given for its recommendations
export const simulationEvidence = (report: SimulationReport, snapshot: SimulationSnapshot): SimulationEvidence => {
    const hours = (seconds: number) => Math.round(seconds/3600*10)/10
    return {
        days: Math.round(Math.max(0, snapshot.tick - 1)/snapshot.ticksPerDay*10)/10,
        health: report.health,
        headline: report.headline,
        itemsIn: report.received,
        outputsProduced: report.produced,
        outputsExpected: report.expected,
        itemsWaiting: report.queued,
        bottlenecks: report.bottlenecks.map(b => ({
            step: b.name, model: b.model, waiting: b.queue, growthPerDay: Math.round(b.growthPerDay*10)/10,
            waitHours: isFinite(b.waitSeconds) ? hours(b.waitSeconds) : null, cause: b.cause, explanation: b.explanation, manual: b.manual, roles: b.roles,
        })),
        outputs: report.outputs.map(o => ({
            output: o.name, model: o.model, produced: o.produced, expected: o.expected, delayHours: hours(o.delaySeconds),
            ...(o.waitStepSeconds ? { waitStepHours: hours(o.waitStepSeconds), waitsAt: o.waitsAt.map(s => s.name) } : {}),
            heldUpBy: o.heldUpBy.map(s => s.name), reason: o.reason,
        })),
        roles: report.roles.map(r => ({
            role: r.name, workVsCapacityPercent: Math.round(r.utilisation*100), shortHoursPerYear: r.extraHours, morePeopleAtOnce: r.extraPeople,
            steps: r.steps.map(s => s.name),
        })),
    }
}
